// ==============================================
// Package & Subscription Types
// ==============================================

// Available basemap sources that can be enabled per package
export type BasemapSource = 'osm' | 'sentinel' | 'esri' | 'azure' | 'google' | 'bing' | 'mapbox' | 'here' | 'gebco' | 'openseamap' | 'navionics';

export interface Package {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  monthly_price_cents: number;
  included_tokens: number;       // Monthly token allocation (multiply by 3 for quarterly quota)
  cost_per_1000_tokens_cents: number;
  features: string[];
  priority_generation: boolean;
  api_access: boolean;
  weather_gfs: boolean;
  weather_ecmwf: boolean;
  weather_wavewatch: boolean;
  weather_briefing: boolean;
  forecast_duration_hours: number;
  ais_integration: boolean;
  bathymetry: boolean;
  nav_library?: boolean;
  screenshot_library?: boolean;
  screenshot_quota_mb?: number;
  public_tracking?: boolean;
  anchor_remote_view?: boolean;
  /** External NMEA sources: TrueHelm, AIS and the instrument panel need it. */
  external_nmea?: boolean;
  // Vortex Anchor & Tide features
  vortex_status_panel: boolean;
  vortex_tide_prediction_days: number;
  vt_tide_alerts: boolean;
  // Chart provider gates
  siitech_marine_enc: boolean;
  tide_watch: boolean;
  instrument_panel: boolean;
  truehelm: boolean;
  vortex_anchor: boolean;
  sd_card_charts: boolean;
  google_drive_charts?: boolean;
  sd_card_manager?: boolean;
  document_library?: boolean;
  document_quota_mb?: number;
  app_library?: boolean;
  // Google web view: the plan's Scouts switch to Google's Android System WebView at boot.
  google_webview?: boolean;
  // Scout diagnostics: the plan's Scouts show their diagnostic extras (addresses, IMEIs, GPS's System, Record and Tuning,
  // Battery's readings and time estimate, Device status's What's included and role).
  scout_diagnostics?: boolean;
  // PassageWiki weather gates: the Colour pulldown, and days of forecast on the slider.
  weather_colour?: boolean;
  weather_days?: number;
  visibility: 'active' | 'hidden' | 'archived';
  sort_order: number;
  is_popular: boolean;
  // When true, subscriptions on this package never expire automatically.
  // Use for tester / internal packages that aren't billed via Stripe.
  manual_expiry: boolean;
  // Devices per subscription; 0 = unlimited.
  max_devices?: number;
  /** Top-up packs this plan sells, and what it does when a boat runs out (set here, never in code). */
  token_packs?: import('../components/admin/PackageTokenSettings').TokenPack[];
  out_of_tokens_policy?: import('../components/admin/PackageTokenSettings').OutOfTokensPolicy | null;
  out_of_tokens_amount?: number | null;
  // Map/chart restrictions
  max_zoom_level: number;
  allowed_basemaps: BasemapSource[];
  allowed_basemaps_offline: BasemapSource[];
  offline_packs: boolean;
  // Stripe integration
  stripe_product_id: string | null;
  stripe_price_id: string | null;
  created_at: string;
  updated_at: string;
}

export interface Subscription {
  id: string;
  user_id: string;
  package_id: string;
  stripe_subscription_id: string | null;
  stripe_customer_id: string | null;
  status: 'active' | 'past_due' | 'canceled' | 'trialing' | 'incomplete';
  current_period_start: string;
  current_period_end: string;
  cancel_at_period_end: boolean;
  tokens_used: number;
  tokens_rolled_over: number;
  created_at: string;
  updated_at: string;
  // Joined data
  package?: Package;
}

export interface UsageRecord {
  id: string;
  user_id: string;
  subscription_id: string | null;
  tokens_used: number;
  billing_period_start: string;
  billing_period_end: string;
  order_id: string | null;
  created_at: string;
}

/** One source's map tiles for the month: viewed live, downloaded, and both. */
export interface SourceTileUsage {
  source: string;
  live: number;
  download: number;
  total: number;
}

/** Tiles served to customers, and tiles fetched from a provider (what we pay for). */
export interface TilePair { served: number; fetched: number }

/** GET /api/admin/tiles/usage — every customer's tiles for a UTC calendar month. */
export interface AdminTileUsage {
  month: string;
  total: TilePair;
  users: Array<{
    userId: string;
    email: string | null;
    name: string | null;
    sources: Array<{ source: string; live: TilePair; download: TilePair; total: TilePair }>;
    live: TilePair;
    download: TilePair;
    total: TilePair;
  }>;
}

/** One day of the Esri check (GET /api/admin/tiles/reconciliation). */
export interface ReconciledDay { date: string; billed: number; frontDoor: number; scouts: number; unexplained: number; flagged: boolean }
export interface TileReconciliation { days: ReconciledDay[]; total: { billed: number; frontDoor: number; scouts: number; unexplained: number } }

/** GET /api/tiles/usage — the signed-in customer's tiles for a UTC calendar month. */
export interface MyTileUsage {
  month: string;
  sources: SourceTileUsage[];
  live: number;
  download: number;
  total: number;
}

export interface UsageSummary {
  subscription: Subscription | null;
  package: Package | null;
  tokens_included: number;
  tokens_rolled_over: number;
  tokens_used: number;
  tokens_remaining: number;
  usage_percentage: number;
  current_period_start: string;
  current_period_end: string;
  days_remaining: number;
  device_count?: number;
  /** Present only for a customer the token ledger is switched on for. */
  tokens_standard_remaining?: number;
  tokens_topup_remaining?: number;
  /** What is owed and what the plan's run-out rule leaves; ledger customers only. */
  tokens_run_out?: TokenRunOut;
}

/** The plan's run-out rule applied to this customer's balance (the API's runOutView). */
export interface TokenRunOut {
  policy: import('../components/admin/PackageTokenSettings').OutOfTokensPolicy | null;
  amount: number | null;
  /** What downloads may use now, overdraft included. */
  spendable: number;
  /** Used beyond zero and not yet paid back. */
  owed: number;
  overdraft_left: number | null;
  grace_ends_at: string | null;
  /** Paid live charts show "Out of tokens". */
  live_paused: boolean;
  /** Overdraft, at zero: paused until the skipper chooses the overdraft on the Scout (3 Nov 2026). */
  live_needs_consent?: boolean;
}

export interface InvoiceLineItem {
  description: string;
  quantity: number;
  unit_price_cents: number;
  total_cents: number;
}

export interface Invoice {
  id: string;
  user_id: string;
  subscription_id: string | null;
  order_id: string | null;
  stripe_invoice_id: string | null;
  stripe_charge_id: string | null;
  // Amounts
  subtotal_cents: number;
  tax_cents: number;
  total_cents: number;
  amount_paid_cents: number;
  currency: string;
  status: 'draft' | 'open' | 'paid' | 'void' | 'uncollectible';
  // Invoice details
  invoice_number: string | null;
  description: string | null;
  invoice_date: string | null;
  due_date: string | null;
  period_start: string | null;
  period_end: string | null;
  invoice_pdf_url: string | null;
  hosted_invoice_url: string | null;
  // NZ GST fields
  gst_number: string | null;
  gst_rate: number | null;
  gst_inclusive: boolean;
  subtotal_excl_gst_cents: number | null;
  // Customer details (snapshot at invoice time)
  customer_name: string | null;
  customer_email: string | null;
  customer_company: string | null;
  customer_address: string | null;
  customer_country: string | null;
  // Company details (snapshot at invoice time)
  company_name: string | null;
  company_address: string | null;
  company_gst_number: string | null;
  // Line items
  line_items: InvoiceLineItem[];
  // Payment tracking
  payment_method: string | null;
  payment_reference: string | null;
  // Receipt tracking
  receipt_number: string | null;
  receipt_issued_at: string | null;
  // Timestamps
  created_at: string;
  paid_at: string | null;
  invoice_emailed_at: string | null;
  receipt_emailed_at: string | null;
}

export interface CompanySettings {
  id: string;
  company_name: string;
  trading_name: string | null;
  address_line1: string | null;
  address_line2: string | null;
  city: string | null;
  region: string | null;
  postal_code: string | null;
  country: string;
  phone: string | null;
  email: string | null;
  website: string | null;
  // Tax details (NZ specific)
  gst_number: string | null;
  gst_rate: number;
  gst_enabled: boolean;
  // Invoice settings
  invoice_prefix: string;
  invoice_next_number: number;
  invoice_footer_text: string | null;
  invoice_payment_terms: string | null;
  // Bank details
  bank_name: string | null;
  bank_account_name: string | null;
  bank_account_number: string | null;
  // Timestamps
  created_at: string;
  updated_at: string;
}

/** Stripe Customer payment methods from `GET /api/users/me/payment-methods` */
export interface PaymentMethod {
  id: string;
  type: 'card' | 'link';
  brand: string | null;
  last_four: string;
  exp_month: number;
  exp_year: number;
  is_default: boolean;
  /** Link wallet email; null for cards */
  email: string | null;
  user_id?: string;
  stripe_payment_method_id?: string;
  created_at?: string;
}

export interface PricingCalculation {
  baseFee: number;
  tilesPrice: number;
  total: number;
  tokensFromQuota: number;
  tokensOverage: number;
}

// All packages are billed quarterly (monthly price × 3, paid every 3 months)
export const BILLING_PERIOD_MONTHS = 3;

/**
 * What "Read billed plans from Stripe" found (POST /api/admin/billing/refresh-billed-prices).
 * docs/core/architecture/PLAN_OVERRIDES.md
 */
export interface BilledPricesReport {
  checked: number;
  updated: number;
  /** Platform subscription ids whose Stripe subscription Stripe does not have. */
  notInStripe: string[];
  /** Live here, but Stripe ended the subscription they point to. */
  stripeEnded: Array<{ id: string; stripe_subscription_id: string; stripe_status: string; user_id: string | null; user_email: string | null }>;
  /** Removed here (e.g. "No subscription" in admin), but Stripe is still charging for it. */
  removedButCharging: Array<{ id: string; stripe_subscription_id: string; stripe_status: string; user_id: string | null; user_email: string | null }>;
  /** Stripe is charging, and no subscription here points to it. */
  unlinked: Array<{
    stripe_subscription_id: string;
    status: string;
    price: string | null;
    customer: string | null;
    customer_email: string | null;
    user_id: string | null;
    user_email: string | null;
  }>;
}


/** One entry in a plan's change history (GET /api/packages/admin/:id/changes), newest first. */
export interface PackageChange {
  id: string;
  action: 'package.created' | 'package.updated';
  changed_at: string | null;
  /** The admin who made the change; null if not recorded or since deleted. */
  changed_by: { id: string; name: string | null; email: string } | null;
  old_values: Record<string, unknown> | null;
  new_values: Record<string, unknown>;
  reason: string | null;
}
